/**
 * ClientOnboardWizard — SB richtet einen Mandanten komplett ein (Batch 4, Juni 2026).
 * 4 Schritte: Stammdaten → Adresse & Steuern → Vollmachten → Zusammenfassung.
 * Kanzlei übernimmt die Pro-Lizenz (license_mode='sb_paid').
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, Mail, Building2, ShieldCheck, BadgeCheck } from 'lucide-react';
import { PostcodeInput, VATInput } from '@/components/inputs/SmartInputs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = { fontFamily: 'Manrope, sans-serif' };

const STEPS = ['Stammdaten', 'Adresse & Steuern', 'Vollmachten', 'Zusammenfassung'];

const DEFAULT_FORM = {
  name: '',
  email: '',
  company_name: '',
  country: 'DE',
  street: '',
  street_number: '',
  postal_code: '',
  city: '',
  tax_number: '',
  vat_id: '',
  is_kleinunternehmer: false,
  tax_scheme: 'cash_basis',
  vat_filing_frequency: 'quarterly',
  permissions: { dashboard: true, invoices: true, expenses: true, receipts: true, taxes: true, edit: false },
};

const PERMISSION_LABELS = {
  dashboard: 'Übersicht & Kennzahlen',
  invoices: 'Rechnungen einsehen',
  expenses: 'Ausgaben einsehen',
  receipts: 'Belege einsehen & downloaden',
  taxes: 'Steuerdaten einsehen',
  edit: 'Bearbeiten-Vollmacht (Kategorien, Korrekturen)',
};

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value || '—'}</span>
    </div>
  );
}

export const ClientOnboardWizard = ({ open, onClose, onSuccess, authHeaders }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [acceptCosts, setAcceptCosts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setPerm = (key, value) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: value } }));

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const step0Valid = form.name.trim().length > 1 && emailValid;

  const reset = () => { setStep(0); setForm(DEFAULT_FORM); setAcceptCosts(false); setDone(false); setSubmitting(false); };
  const handleClose = () => { reset(); onClose(); };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/advisor/clients/onboard`, {
        ...form,
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        company_name: form.company_name.trim() || null,
        street: form.street.trim() || null,
        street_number: form.street_number.trim() || null,
        postal_code: form.postal_code.trim() || null,
        city: form.city.trim() || null,
        tax_number: form.tax_number.trim() || null,
        vat_id: form.vat_id.trim() || null,
        accept_license_costs: acceptCosts,
      }, { headers: authHeaders });
      setDone(true);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mandant konnte nicht eingerichtet werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4" data-testid="onboard-wizard-overlay">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl max-h-[90vh] overflow-y-auto" data-testid="onboard-wizard">
        {done ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900" style={H}>Mandant eingerichtet</h3>
            <p className="mt-2 text-sm text-slate-500">
              <strong>{form.name}</strong> hat eine E-Mail mit dem Aktivierungslink erhalten
              (7 Tage gültig). Sobald das Passwort gesetzt ist, erscheint der Mandant als „Aktiv".
            </p>
            <Button onClick={handleClose} className="mt-6 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="onboard-done-close-btn">
              Fertig
            </Button>
          </div>
        ) : (
          <div className="p-6">
            {/* Header + Stepper */}
            <div className="mb-5">
              <h3 className="text-lg font-semibold tracking-tight text-slate-900" style={H}>Mandant anlegen</h3>
              <p className="text-xs text-slate-500 mt-0.5">Du richtest den Account komplett ein — deine Kanzlei übernimmt die Pro-Lizenz, dein Mandant muss nur noch sein Passwort festlegen.</p>
              <div className="mt-4 flex items-center gap-1">
                {STEPS.map((label, i) => (
                  <React.Fragment key={label}>
                    <div className={`flex items-center gap-1.5 ${i === step ? 'text-emerald-700' : i < step ? 'text-emerald-500' : 'text-slate-400'}`}>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${i === step ? 'bg-emerald-600 text-white' : i < step ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                        {i + 1}
                      </span>
                      <span className="text-[11px] font-medium hidden sm:inline">{label}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Step 0: Stammdaten */}
            {step === 0 && (
              <div className="space-y-4" data-testid="onboard-step-stammdaten">
                <FieldRow label="Name des Mandanten *">
                  <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Max Mustermann" data-testid="onboard-name-input" />
                </FieldRow>
                <FieldRow label="E-Mail-Adresse *">
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="max@beispiel.de" data-testid="onboard-email-input" />
                </FieldRow>
                <FieldRow label="Firma / Geschäftsbezeichnung">
                  <Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Mustermann Consulting" data-testid="onboard-company-input" />
                </FieldRow>
                <FieldRow label="Land *">
                  <select
                    value={form.country}
                    onChange={(e) => set('country', e.target.value)}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                    data-testid="onboard-country-select"
                  >
                    <option value="DE">🇩🇪 Deutschland</option>
                    <option value="AT">🇦🇹 Österreich</option>
                    <option value="CH">🇨🇭 Schweiz</option>
                  </select>
                </FieldRow>
              </div>
            )}

            {/* Step 1: Adresse & Steuern */}
            {step === 1 && (
              <div className="space-y-4" data-testid="onboard-step-steuern">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <FieldRow label="Straße">
                      <Input value={form.street} onChange={(e) => set('street', e.target.value)} data-testid="onboard-street-input" />
                    </FieldRow>
                  </div>
                  <FieldRow label="Nr.">
                    <Input value={form.street_number} onChange={(e) => set('street_number', e.target.value)} data-testid="onboard-street-number-input" />
                  </FieldRow>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FieldRow label="PLZ">
                    <PostcodeInput country={form.country} value={form.postal_code} onChange={(v) => set('postal_code', v)} lookupCity onCityFound={(city) => !form.city && set('city', city)} data-testid="onboard-postal-input" />
                  </FieldRow>
                  <div className="col-span-2">
                    <FieldRow label="Ort">
                      <Input value={form.city} onChange={(e) => set('city', e.target.value)} data-testid="onboard-city-input" />
                    </FieldRow>
                  </div>
                </div>
                <FieldRow label="Steuernummer">
                  <Input value={form.tax_number} onChange={(e) => set('tax_number', e.target.value)} placeholder={form.country === 'AT' ? '99 999/9999' : '12/345/67890'} data-testid="onboard-tax-number-input" />
                </FieldRow>
                <FieldRow label={form.country === 'CH' ? 'UID / MWST-Nummer' : 'USt-IdNr.'}>
                  <VATInput country={form.country} value={form.vat_id} onChange={(v) => set('vat_id', v)} data-testid="onboard-vat-input" />
                </FieldRow>
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Kleinunternehmer</p>
                    <p className="text-[11px] text-slate-500">{form.country === 'AT' ? '§ 6 Abs. 1 Z 27 UStG' : form.country === 'DE' ? '§ 19 UStG' : 'Von der MWST befreit'}</p>
                  </div>
                  <Switch checked={form.is_kleinunternehmer} onCheckedChange={(v) => set('is_kleinunternehmer', v)} data-testid="onboard-ku-switch" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Versteuerung">
                    <select value={form.tax_scheme} onChange={(e) => set('tax_scheme', e.target.value)} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="onboard-tax-scheme-select">
                      <option value="cash_basis">Ist-Versteuerung</option>
                      <option value="accrual">Soll-Versteuerung</option>
                    </select>
                  </FieldRow>
                  <FieldRow label="UStVA-Rhythmus">
                    <select value={form.vat_filing_frequency} onChange={(e) => set('vat_filing_frequency', e.target.value)} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="onboard-vat-frequency-select">
                      <option value="monthly">Monatlich</option>
                      <option value="quarterly">Quartalsweise</option>
                      <option value="yearly">Jährlich</option>
                    </select>
                  </FieldRow>
                </div>
              </div>
            )}

            {/* Step 2: Vollmachten */}
            {step === 2 && (
              <div className="space-y-3" data-testid="onboard-step-vollmachten">
                <p className="text-xs text-slate-500">
                  Diese Berechtigungen gelten ab sofort. Dein Mandant wird in der Aktivierungs-Mail
                  explizit darüber informiert und kann sie jederzeit in seinen Einstellungen
                  einschränken oder widerrufen.
                </p>
                {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      {key === 'edit' ? <ShieldCheck className="h-4 w-4 text-amber-500" /> : <BadgeCheck className="h-4 w-4 text-slate-400" />}
                      <span className="text-sm text-slate-800">{label}</span>
                    </div>
                    <Switch checked={form.permissions[key]} onCheckedChange={(v) => setPerm(key, v)} data-testid={`onboard-perm-${key}-switch`} />
                  </div>
                ))}
              </div>
            )}

            {/* Step 3: Zusammenfassung */}
            {step === 3 && (
              <div data-testid="onboard-step-summary">
                <div className="rounded-lg border border-slate-200 px-4 py-2 divide-y divide-slate-100">
                  <SummaryRow label="Name" value={form.name} />
                  <SummaryRow label="E-Mail" value={form.email} />
                  <SummaryRow label="Firma" value={form.company_name} />
                  <SummaryRow label="Land" value={form.country} />
                  <SummaryRow label="Ort" value={[form.postal_code, form.city].filter(Boolean).join(' ')} />
                  <SummaryRow label="Kleinunternehmer" value={form.is_kleinunternehmer ? 'Ja' : 'Nein'} />
                  <SummaryRow label="Versteuerung" value={form.tax_scheme === 'accrual' ? 'Soll' : 'Ist'} />
                  <SummaryRow label="Vollmachten" value={Object.entries(form.permissions).filter(([, v]) => v).length + ' aktiv'} />
                </div>
                <div className="mt-4 flex items-start gap-3 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3" data-testid="onboard-license-hint">
                  <Building2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-emerald-900">
                    <strong>Kanzlei-Lizenz:</strong> Deine Kanzlei übernimmt die Pro-Lizenz für diesen
                    Mandanten (16 €/Monat — jeder angefangene Monat zählt voll). Der Mandant erhält
                    vollen Pro-Zugang ohne eigene Zahlung.
                  </p>
                </div>
                <label className="mt-3 flex items-start gap-3 rounded-lg border border-slate-200 px-4 py-3 cursor-pointer" data-testid="onboard-cost-consent-label">
                  <Checkbox
                    checked={acceptCosts}
                    onCheckedChange={(v) => setAcceptCosts(!!v)}
                    className="mt-0.5"
                    data-testid="onboard-cost-consent-checkbox"
                  />
                  <span className="text-xs text-slate-600">
                    Ich übernehme die Kosten der Pro-Lizenz für diesen Mandanten (16 €/Monat) gemäß{' '}
                    <a href="/steuerberater-agb" target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline hover:text-emerald-700" data-testid="onboard-terms-link">
                      ivoliq SB-Vertrag
                    </a>.
                  </span>
                </label>
                <div className="mt-3 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <Mail className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-600">
                    Nach dem Erstellen erhält <strong>{form.email || 'dein Mandant'}</strong> eine E-Mail mit
                    einem Aktivierungslink (7 Tage gültig) zum Passwort-Festlegen — inkl. Hinweis auf
                    die erteilten Vollmachten.
                  </p>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-6 flex items-center justify-between">
              <Button variant="ghost" onClick={step === 0 ? handleClose : () => setStep(step - 1)} data-testid="onboard-back-btn">
                {step === 0 ? 'Abbrechen' : <><ChevronLeft className="mr-1 h-4 w-4" /> Zurück</>}
              </Button>
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={step === 0 && !step0Valid}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="onboard-next-btn"
                >
                  Weiter <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={submitting || !acceptCosts} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="onboard-submit-btn">
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Mandant anlegen & einladen
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientOnboardWizard;
